import * as React from 'react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

interface RadioCardGroupProps {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}

/**
 * Radix moves focus on arrow keys but only commits on Space/Enter. Native radio
 * groups — what MUI rendered — select whatever the arrows land on, so selection
 * is driven from focus to keep that behaviour.
 */
const SelectOnFocus = React.createContext<((value: string) => void) | null>(null);

/**
 * Exclusive choice list standing in for MUI's RadioGroup, which has no Radix
 * package installed here. `type="single"` gives every item role="radio" inside
 * the group, so the choice keeps its semantics.
 */
const RadioCardGroup: React.FC<RadioCardGroupProps> = ({
  value,
  onValueChange,
  className,
  children,
}) => (
  <ToggleGroup
    type="single"
    value={value}
    // Radix reports '' when the pressed item is already the active one; a radio
    // group can never be emptied that way, so the current value has to stand.
    onValueChange={(next) => {
      if (next) onValueChange(next);
    }}
    className={cn('flex flex-col items-stretch justify-start gap-2', className)}
  >
    <SelectOnFocus.Provider value={onValueChange}>{children}</SelectOnFocus.Provider>
  </ToggleGroup>
);

interface RadioCardProps {
  value: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

const RadioCard: React.FC<RadioCardProps> = ({
  value,
  label,
  description,
  disabled,
  className,
}) => {
  const selectOnFocus = React.useContext(SelectOnFocus);

  return (
  <ToggleGroupItem
    value={value}
    disabled={disabled}
    onFocus={() => selectOnFocus?.(value)}
    className={cn(
      'group h-auto justify-start gap-3 rounded-md px-3 py-2 text-left hover:text-foreground',
      'data-[state=on]:bg-primary/5 data-[state=on]:text-foreground',
      className,
    )}
  >
    <span className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border border-primary">
      <span className="size-2 rounded-full bg-primary opacity-0 transition-opacity group-data-[state=on]:opacity-100" />
    </span>
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="revy-cjk text-sm font-normal leading-[1.5]">{label}</span>
      {description && (
        <span className="revy-cjk text-xs leading-[1.5] text-muted-foreground">{description}</span>
      )}
    </span>
  </ToggleGroupItem>
  );
};

export { RadioCardGroup, RadioCard };
